/**
 * @file pregnancy.schema.ts
 * @description Define o schema Zod para a entidade 'Pregnancy'.
 * Ele modela todos os aspectos de uma gestação, incluindo dados clínicos
 * (como a DUM e idade gestacional), informações do bebê, um sistema de
 * controle de acesso granular para convidados ('Guests'), e um ponteiro
 * para o último conteúdo consumido para uma melhor experiência do usuário.
 */

import { z } from 'zod';
import { ApproximateDateSchema } from './approximate-date.schema';

// --- Helper Schemas & Enums ---

/**
 * Define os status possíveis de uma gestação.
 * Como são estados, usamos lowercase.
 */
const PregnancyStatusEnum = z.enum(['active', 'completed', 'interrupted']);

/**
 * Em que ponto do cuidado a perda gestacional aconteceu.
 *
 * **Preenchido por profissional.** Até 02/10/2026 a regra era também nunca
 * perguntar à mulher. A reunião de sprint de 02/10 mudou isso: o onboarding
 * de perdi meu bebê pergunta, sempre opcional, e a resposta dela fica em
 * `clinicalProfile.perdaGestacional` — campo separado, com as faixas que o
 * time clínico usa nos checkpoints. Este aqui continua sendo o do
 * profissional, pelo prontuário.
 *
 * As faixas seguem a divisão obstétrica usual. `UNKNOWN` existe porque não
 * saber é estado legítimo e frequente: a mulher pode chegar à Eva já depois da
 * perda, sem que ninguém tenha registrado quando foi.
 */
const LossCarePointEnum = z.enum([
  'FIRST_TRIMESTER',   // até 13 semanas
  'SECOND_TRIMESTER',  // 14 a 27 semanas
  'THIRD_TRIMESTER',   // 28 semanas em diante, anteparto
  'NEONATAL',          // após o nascimento
  'UNKNOWN',
]);

/**
 * Define as opções para o sexo do bebê.
 * Como é uma categoria fixa, usamos UPPER_SNAKE_CASE.
 */
const BabySexEnum = z.enum(['MALE', 'FEMALE', 'UNKNOWN']);

/**
 * Define a estrutura para informações de um bebê individual.
 * Suporta múltiplos bebês (gêmeos, trigêmeos, etc.).
 */
const BabySchema = z.object({
  name: z.string().nullable(),
  sex: BabySexEnum,
});

/**
 * Define os níveis de permissão que um convidado pode ter em uma funcionalidade.
 * Como são estados de permissão, usamos lowercase.
 */
const AccessLevelEnum = z.enum(['hide', 'read', 'edit']);

/**
 * Define a estrutura de permissões granulares para um convidado.
 */
const GuestPermissionsSchema = z.object({
  activities: AccessLevelEnum,
  chat: AccessLevelEnum,
  content: AccessLevelEnum,
});

/**
 * Idade gestacional forçada/ajustada por profissional, representando o valor atual de semanas e dias.
 */
/**
 * Idade gestacional forçada/ajustada por profissional, representando o valor atual de semanas e dias
 * em uma data de referência específica.
 */
const EnforcedGestationalAgeSchema = z.object({
  weeks: z.number().int().min(0).max(45),
  days: z.number().int().min(0).max(6),
  referenceDate: z.date(), // Data em que essa idade foi aferida (ex: data do USG)
  source: z.enum(['ULTRASOUND', 'CLINICAL_EXAM']).optional(),
});

/**
 * Define os períodos de um mês para uma data aproximada.
 */
/**
 * A DUM é uma data aproximada como qualquer outra — a forma mora em
 * `approximate-date.schema.ts`, reusada por "desde quando tenta engravidar" e
 * "desde quando sem método". Aqui fica só o alias, para não quebrar quem
 * importa `LmpDateSchema`.
 */
const LmpDateSchema = ApproximateDateSchema;

/**
 * Define a estrutura para o ponteiro do último conteúdo acessado pela usuária.
 */
const LastConsumedContentSchema = z.object({
  // ID do documento na coleção 'content' ou de um vídeo dentro de um curso
  contentId: z.string(),
  // Tipo do conteúdo para saber como renderizar o link "Continuar assistindo"
  lastAccessedAt: z.date(),
});

/**
 * Metadados sobre a revisão de uma seção de risco (se o médico revisou, se tem achados).
 */
const RiskUpdateMetadataSchema = z.object({
  reviewed: z.boolean(),
  hasFindings: z.boolean(),
});

/**
 * Representa um fator de risco selecionado.
 */
/**
 * Fator de risco marcado NESTA gestação — só os de escopo PREGNANCY do
 * catálogo em `risk-factor.schema.ts`. Os de escopo WOMAN vão para
 * `clinicalProfiles.riskFactors`, e os DERIVED (idade) não são gravados.
 *
 * `category` é string livre e guardava o título do grupo de exibição, que não
 * diz nada sobre escopo. Continua aceito para não quebrar leitura, mas o
 * escopo se consulta pelo código, no catálogo.
 */
const RiskFactorSchema = z.object({
  code: z.string(),
  category: z.string().optional(),
  label: z.string(),
  notes: z.string().nullable().optional(),
});

/**
 * Dados clínicos e anamnese essenciais para cálculos de risco e gráficos de acompanhamento.
 */
const ClinicalBaselineSchema = z.object({
  // --- LEGADO: estes quatro são da MULHER, não da gestação ------------------
  // O destino é `clinicalProfiles` (clinical-profile.schema.ts). Continuam
  // aceitos aqui porque estamos na fase EXPAND do expand/contract: o código lê
  // dos dois lugares até o backfill rodar. Não grave neles em código novo —
  // use `mergeClinicalBaseline` para ler.
  /** @deprecated mora em `clinicalProfiles.heightCm` */
  heightCm: z.number().positive().optional(),
  /** @deprecated mora em `clinicalProfiles.bloodType` */
  bloodType: z.enum(['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'UNKNOWN']).nullable().optional(),
  /** @deprecated mora em `clinicalProfiles.allergies` */
  allergies: z.array(z.string()).default([]),
  /** @deprecated mora em `clinicalProfiles.comorbidities` */
  comorbidities: z.array(z.string()).default([]),

  // --- Desta gestação -------------------------------------------------------
  // O peso ANTES de cada gestação é diferente: é da gestação, apesar de parecer
  // da mulher.
  prePregnancyWeightKg: z.number().positive().optional(),

  // -- Legacy / Manual Risk --
  riskClassification: z.enum(['LOW', 'HIGH', 'HABITUAL']).optional().nullable(),

  // -- Structured Risk Assessment (MVP) --
  riskStatus: z.enum(['HIGH', 'HABITUAL']).optional(),
  generatedSummary: z.string().optional(),
  riskLastUpdateAt: z.coerce.date().optional(), // Coerce handles string -> Date
  riskFactors: z.array(RiskFactorSchema).default([]),
  riskSectionsMetadata: z.record(RiskUpdateMetadataSchema).optional(),

  obstetricDiagnostics: z.string().nullable().optional(), // Campo aberto para diagnósticos
  medications: z.string().nullable().optional(), // Campo aberto para medicações
  habits: z.array(z.string()).default([]), // Campo para Hábitos
});


const MeasurementTypeEnum = z.enum(['weight_bmi', 'fundal_height', 'blood_pressure', 'hemoglobin', 'map', 'estimated_fetal_weight', 'fetal_heart_rate', 'crl', 'bpd', 'head_circumference', 'abdominal_circumference', 'femur_length', 'glucose_fasting', 'glucose_1h', 'glucose_2h']);

const MeasurementContextSchema = z.object({
  gestationalAge: z.object({ weeks: z.number(), days: z.number() }).optional(),
  heightCm: z.number().optional(),
  prePregnancyWeightKg: z.number().optional(),
  systolic: z.number().optional(),
  diastolic: z.number().optional(),
  // Add other context fields if needed
});

const MeasurementSchema = z.object({
  id: z.string().optional(),
  date: z.coerce.date(),
  type: MeasurementTypeEnum,
  value: z.number(),
  context: MeasurementContextSchema.optional(),
  recordedBy: z.string().optional()
});

// --- Birth Plan Schema ---

export const BirthPlanDeliveryModeEnum = z.enum(['NORMAL', 'NATURAL', 'CESAREAN']);
export const BirthPlanPlaceEnum = z.enum(['HOSPITAL', 'HOME']);
export const BirthPlanCompanionRoleEnum = z.enum(['PARTNER', 'DOULA', 'FAMILY', 'OTHER']);
export const BirthPlanAnalgesiaEnum = z.enum(['YES', 'NO', 'UNDECIDED']);

export const BirthPlanCompanionSchema = z.object({
  name: z.string(),
  role: BirthPlanCompanionRoleEnum,
});

export const BirthPlanCareTeamRoleEnum = z.enum(['OBSTETRICIAN', 'OBSTETRIC_NURSE', 'DOULA', 'MIDWIFE', 'PEDIATRICIAN', 'ANESTHESIOLOGIST', 'OTHER']);

export const BirthPlanCareTeamMemberSchema = z.object({
  name: z.string(),
  role: BirthPlanCareTeamRoleEnum,
  contact: z.string().optional(),
});

export const BirthPlanSchema = z.object({
  careTeam: z.array(BirthPlanCareTeamMemberSchema).default([]),
  birthPlace: BirthPlanPlaceEnum.optional(),
  maternityName: z.string().optional(),
  maternityCity: z.string().optional(),
  maternityState: z.string().optional(),
  deliveryMode: BirthPlanDeliveryModeEnum,
  companions: z.array(BirthPlanCompanionSchema),
  analgesia: BirthPlanAnalgesiaEnum.optional(),
  goldenHour: z.boolean(),
  environmentPrefs: z.string().optional(),
  babyCarePrefs: z.string().optional(),
  contingencyPlan: z.string().optional(),
  cesareanDate: z.date().nullable().optional(),
  hasHealthInsurance: z.enum(['YES', 'NO']).optional(),
  healthInsuranceName: z.string().optional(),
  attachedDocument: z.object({
    url: z.string(),
    name: z.string(),
    type: z.string(),
  }).nullable().optional(),
});

// --- Main Pregnancy Schema ---

/**
 * Define o schema principal e completo para a entidade Pregnancy.
 */
const PregnancySchema = z.object({
  id: z.string(), // O ID é injetado pelo Firestore ou gerado no Backend
  // Chave estrangeira para a entidade User (PregnantWoman)
  pregnantWomanId: z.string(),

  // Cópia do ID do parceiro da gestante para facilitar regras de acesso.
  partnerId: z.string().nullable().optional(),

  // Status atual da gestação
  status: PregnancyStatusEnum,

  // Data da Última Menstruação (DUM), pode ser exata, aproximada ou desconhecida.
  lmpDate: LmpDateSchema,

  // Data Provável do Parto (DPP)
  dueDate: z.date().nullable(),

  /**
   * Só faz sentido quando `status === 'interrupted'`. Ver `LossCarePointEnum`:
   * é o único dado da jornada Perdi meu bebê que a Eva coleta hoje, e ele
   * entra pelo profissional.
   */
  lossCarePoint: LossCarePointEnum.optional(),

  // Data real do parto (preenchido para puérperas/completed)
  // coerce: tolera registros legados gravados como string ISO (ex.: "2026-05-01")
  childbirthDate: z.coerce.date().nullable().optional(),

  // Idade gestacional imposta por um profissional (ex: ultrassom), pode não existir.
  enforcedGestationalAge: EnforcedGestationalAgeSchema.nullable(),

  // Quando true, a gestante não pode alterar a idade gestacional no app
  // (apenas admins/profissionais podem). Útil quando a IG foi validada clinicamente.
  gestationalAgeLocked: z.boolean().optional().default(false),

  // Ponteiro para o último conteúdo que a usuária consumiu nesta gestação.
  lastConsumedContent: LastConsumedContentSchema.nullable(),

  // Motor Clínico: Dados base para cálculos e alertas
  clinicalBaseline: ClinicalBaselineSchema.nullable().optional(),
  // Array de bebês para suportar gêmeos, trigêmeos, etc. Mínimo de 1 bebê.
  babies: z.array(BabySchema).min(1),

  // Nome da gestante, desnormalizado para otimizar leituras em listas.
  gestantePreferredName: z.string(),

  // Objeto com as permissões de acesso para cada convidado.
  sharedWith: z.record(GuestPermissionsSchema),

  // Plano de Parto Estruturado
  birthPlan: BirthPlanSchema.nullable().optional(),

  // Observações Gerais (Texto livre)
  observations: z.string().nullable().optional(),

  // Decisão sobre Cesárea Eletiva (Legacy/Separate flow - keeping for now to avoid huge break, but could be deprecated)
  electiveCSection: z.object({
    decision: z.enum(['YES', 'NO', 'DISCUSSING']).default('DISCUSSING'),
    scheduledDate: z.date().nullable().optional(),
    justification: z.string().nullable().optional(),
  }).nullable().optional(),
  // Campos de auditoria e exclusão lógica.
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),

  // Metadata for Gestational Age Method (Visual Override)
  gestationalAgeMethod: z.enum(['LMP', 'DUE_DATE', 'CONCEPTION', 'IVF', 'ULTRASOUND', 'CLINICAL_EXAM', 'UNKNOWN']).optional(),

  // Registro histórico de medições para gráficos (Peso, Altura Uterina, etc.)
  measurements: z.array(MeasurementSchema).optional(),
});


// --- Inferred TypeScript Types ---

type Pregnancy = z.infer<typeof PregnancySchema>;
type PregnancyStatus = z.infer<typeof PregnancyStatusEnum>;
type BabySex = z.infer<typeof BabySexEnum>;
type Baby = z.infer<typeof BabySchema>;
type AccessLevel = z.infer<typeof AccessLevelEnum>;
type GuestPermissions = z.infer<typeof GuestPermissionsSchema>;
type LmpDate = z.infer<typeof LmpDateSchema>;
type LastConsumedContent = z.infer<typeof LastConsumedContentSchema>;
type Measurement = z.infer<typeof MeasurementSchema>;


// --- Public Exports ---

export {
  PregnancySchema,
  PregnancyStatusEnum,
  LossCarePointEnum,
  BabySexEnum,
  BabySchema,
  AccessLevelEnum,
  GuestPermissionsSchema,
  LmpDateSchema,
  LastConsumedContentSchema,
  ClinicalBaselineSchema,
  MeasurementSchema,
  MeasurementTypeEnum,
  MeasurementContextSchema,
  RiskUpdateMetadataSchema,
  RiskFactorSchema,
};

export type {
  Pregnancy,
  PregnancyStatus,
  BabySex,
  Baby,
  AccessLevel,
  GuestPermissions,
  LmpDate,
  LastConsumedContent,
  ClinicalBaseline,
  Measurement,
};

type ClinicalBaseline = z.infer<typeof ClinicalBaselineSchema>;